/**
 * An on-screen event trail for the match-start path, enabled with `?debug=1`.
 *
 * WHY THIS EXISTS
 * ---------------
 * The lifecycle bugs in this flow (a queued deploy, a match that starts and is
 * immediately replaced by the home screen) leave no visible evidence: the state
 * that distinguishes them lives in the console, and asking a player to open
 * devtools is not a diagnostic path that works. This writes the same events
 * onto the page so a screenshot, or a glance, answers the question.
 *
 *     https://rift.rivi.us/?debug=1
 *
 * Off by default and inert when off — `trace()` still logs, so nothing is lost.
 */

const ENABLED =
  typeof location !== 'undefined' && new URLSearchParams(location.search).get('debug') === '1';

const MAX_LINES = 14;
const lines = [];
let panel = null;

function ensurePanel() {
  if (panel || typeof document === 'undefined') return panel;
  panel = document.createElement('div');
  panel.id = 'rift-trace';
  // pointer-events:none is not optional: this sits over the whole UI, and a
  // transparent sheet that swallows clicks is the exact bug class this file
  // exists to help diagnose.
  panel.style.cssText = [
    'position:fixed', 'left:0', 'top:0', 'z-index:10000',
    'max-width:min(560px,60vw)', 'padding:8px 12px',
    'background:rgba(4,8,12,.86)', 'color:#a8e86a',
    'font:11px/1.45 ui-monospace,monospace', 'letter-spacing:.04em',
    'white-space:pre-wrap', 'pointer-events:none', 'user-select:text',
  ].join(';');
  document.body.appendChild(panel);
  return panel;
}

/** Record one lifecycle event. Always logs; draws on screen under `?debug=1`. */
export function trace(tag, detail) {
  if (detail === undefined) console.info(`[trace] ${tag}`);
  else console.info(`[trace] ${tag}`, detail);
  if (!ENABLED) return;
  const t = new Date();
  const stamp = `${String(t.getMinutes()).padStart(2, '0')}:${String(t.getSeconds()).padStart(2, '0')}.${String(t.getMilliseconds()).padStart(3, '0')}`;
  const text = detail === undefined ? tag : `${tag} — ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`;
  lines.push(`${stamp}  ${text}`);
  if (lines.length > MAX_LINES) lines.shift();
  const el = ensurePanel();
  if (el) el.textContent = lines.join('\n');
}

export const traceEnabled = ENABLED;
